import { useVirtualizer } from '@tanstack/react-virtual';
import { Check } from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useMemo, useRef } from 'react';

import { cx } from '@/lib/cx';
import { trechosDestacados } from '@/lib/texto';
import { Skeleton } from '@/ui/primitives/skeleton';

import { linhasDaLista } from './linhas-lista';
import { type EstadoBusca, type OpcaoPesquisavel } from './use-opcoes-pesquisaveis';

const ALTURA_OPCAO = 36;

interface ListaProps {
  idLista: string;
  rotulo: string;
  opcoes: readonly OpcaoPesquisavel[];
  termo: string;
  ativa: number;
  selecionado: string | null;
  estado: EstadoBusca;
  minCaracteres: number;
  vazio: string;
  idOpcao: (indice: number) => string;
  aoEscolher: (opcao: OpcaoPesquisavel) => void;
  aoApontar: (indice: number) => void;
  tentarDeNovo: () => void;
}

function Rotulo({ texto, termo }: { texto: string; termo: string }) {
  return (
    <span className="truncate">
      {trechosDestacados(texto, termo).map((t, i) =>
        t.destaque ? (
          <mark key={i} className="rounded-mini bg-marca-suave px-0.5 text-marca">
            {t.texto}
          </mark>
        ) : (
          <Fragment key={i}>{t.texto}</Fragment>
        ),
      )}
    </span>
  );
}

function Aviso({ children }: { children: ReactNode }) {
  return <div className="px-3 py-5 text-center text-sm text-texto-fraco">{children}</div>;
}

/** Cabeçalho de grupo: fora da navegação, descreve as opções dele (`aria-describedby`). */
function CabecalhoGrupo({ id, rotulo }: { id: string; rotulo: string }) {
  return (
    <div
      id={id}
      role="presentation"
      className="flex h-full items-end px-2.5 pb-1 text-xs font-medium text-texto-fraco"
    >
      {rotulo}
    </div>
  );
}

interface PropsOpcao {
  id: string;
  opcao: OpcaoPesquisavel;
  ativa: boolean;
  escolhida: boolean;
  termo: string;
  idGrupo: string | undefined;
  aoApontar: () => void;
  aoEscolher: () => void;
}

function Opcao(p: PropsOpcao) {
  return (
    <div
      id={p.id}
      role="option"
      aria-selected={p.escolhida}
      aria-describedby={p.idGrupo}
      data-ativa={p.ativa || undefined}
      onMouseMove={p.aoApontar}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      onClick={p.aoEscolher}
      className="flex h-full cursor-default items-center justify-between gap-2 rounded-controle px-2.5 text-base transition-colors duration-[var(--duracao-rapida)] data-ativa:bg-superficie-selecao"
    >
      <Rotulo texto={p.opcao.rotulo} termo={p.termo} />
      <span className="flex shrink-0 items-center gap-2">
        {p.opcao.detalhe && (
          <span aria-hidden className="font-mono text-xs text-texto-fraco">
            {p.opcao.detalhe}
          </span>
        )}
        {p.escolhida && <Check className="size-4 text-marca" aria-hidden />}
      </span>
    </div>
  );
}

/** Listbox virtualizada: milhares de opções sem pesar o DOM; `grupo` intercala cabeçalhos. */
export function ListaPesquisavel(p: ListaProps) {
  const rolagem = useRef<HTMLDivElement>(null);
  const { linhas, linhaDaOpcao } = useMemo(() => linhasDaLista(p.opcoes), [p.opcoes]);
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual devolve funções não memoizáveis; a lista não depende de memo.
  const virtual = useVirtualizer({
    count: linhas.length,
    getScrollElement: () => rolagem.current,
    estimateSize: () => ALTURA_OPCAO,
    overscan: 8,
  });
  const linhaAtiva = p.ativa >= 0 ? linhaDaOpcao[p.ativa] : undefined;
  useEffect(() => {
    if (linhaAtiva !== undefined) virtual.scrollToIndex(linhaAtiva, { align: 'auto' });
  }, [linhaAtiva, virtual]);

  if (p.estado === 'carregando' && p.opcoes.length === 0)
    return (
      <div className="grid gap-1.5 p-2" aria-busy="true">
        <Skeleton className="h-6" />
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-6 w-1/2" />
      </div>
    );
  if (p.estado === 'curto') return <Aviso>Digite pelo menos {p.minCaracteres} letras</Aviso>;
  if (p.estado === 'erro')
    return (
      <Aviso>
        Não foi possível buscar.{' '}
        <button type="button" className="text-marca hover:underline" onClick={p.tentarDeNovo}>
          Tentar de novo
        </button>
      </Aviso>
    );
  if (p.opcoes.length === 0) return <Aviso>{p.vazio}</Aviso>;

  const idGrupo = (linha: number) => `${p.idLista}-grupo-${String(linha)}`;
  return (
    <div
      ref={rolagem}
      id={p.idLista}
      role="listbox"
      aria-label={p.rotulo}
      className={cx('max-h-64 overflow-y-auto p-1', p.estado === 'carregando' && 'opacity-60')}
    >
      <div className="relative" style={{ height: virtual.getTotalSize() }}>
        {virtual.getVirtualItems().map((item) => {
          const linha = linhas[item.index];
          if (!linha) return null;
          return (
            <div
              key={linha.tipo === 'grupo' ? `grupo-${linha.rotulo}` : linha.opcao.valor}
              role="presentation"
              className="absolute inset-x-0"
              style={{ top: item.start, height: item.size }}
            >
              {linha.tipo === 'grupo' ? (
                <CabecalhoGrupo id={idGrupo(item.index)} rotulo={linha.rotulo} />
              ) : (
                <Opcao
                  id={p.idOpcao(linha.indice)}
                  opcao={linha.opcao}
                  ativa={linha.indice === p.ativa}
                  escolhida={linha.opcao.valor === p.selecionado}
                  termo={p.termo}
                  idGrupo={linha.cabecalho === null ? undefined : idGrupo(linha.cabecalho)}
                  aoApontar={() => {
                    p.aoApontar(linha.indice);
                  }}
                  aoEscolher={() => {
                    p.aoEscolher(linha.opcao);
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
